import * as React from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import { CircularProgress, ListItem, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';
import { useState, useRef } from 'react';
import reshet from '@/assets/reshet.png';
import { pagesList } from '@/constants';
import { routesList } from '@/app/app';
import { useNavigate } from 'react-router-dom';
import ProfileSection from './ProfileSection';
import HoverMenu from './HoverMenu';
import { theme } from '../theme/theme';
import MobileLayout from './MobileDashboardLayout';
import PageTitleHeader from './PageTitleHeader';
import { useAppContext } from '@/context/context';

const drawerWidth = 170;

interface DashboardLayoutProps {
  children: React.ReactNode;
  bgColor?: string;
}

const DashboardLayout = (props: DashboardLayoutProps) => {
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const navigate = useNavigate();
  const closeMenuTimer = useRef<(NodeJS.Timeout | null)[]>(Array(pagesList.length).fill(null));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const appContext = useAppContext();
  const selectedNav = appContext?.selectedNav || { link: '', primary: '' };
  const userPermission = appContext?.userPermission;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, index: number) => {
    if (closeMenuTimer.current[index]) {
      clearTimeout(closeMenuTimer.current[index]!);
      closeMenuTimer.current[index] = null;
    }
    closeMenuTimer.current.forEach((timerId, i) => {
      if (i !== index && timerId) {
        clearTimeout(timerId);
        closeMenuTimer.current[i] = null;
      }
    });
    setOpenMenuIndex(index);
  };

  const handleMenuClose = (index: number, link?: string) => {
    if (closeMenuTimer.current[index]) {
      clearTimeout(closeMenuTimer.current[index]!);
      closeMenuTimer.current[index] = null;
    }
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    }
    if (link) {
      navigate(link);
    }
  };

  const handleMouseLeave = (index: number) => {
    if (openMenuIndex === index) {
      if (closeMenuTimer.current[index]) {
        clearTimeout(closeMenuTimer.current[index]!);
      }
      closeMenuTimer.current[index] = setTimeout(() => {
        handleMenuClose(index);
      }, 250);
    }
  };

  const handleMouseEnterMenu = (index: number) => {
    if (closeMenuTimer.current[index]) {
      clearTimeout(closeMenuTimer.current[index]!);
      closeMenuTimer.current[index] = null;
    }
  };

  const handleInitialization = () => {
    if (!userPermission) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <CircularProgress />
        </Box>
      );
    }

    // בדיקת הרשאות לפי תגיות
    const isBlocked = () => {
      if (userPermission && userPermission.isAdmin) {
        return false;
      }

      if (!userPermission || !selectedNav.link) {
        return true;
      }

      // בדיקה אם הנתיב הוא ציבורי
      const currentRoute = routesList.find(route => route.route === selectedNav.link);
      // אם הנתיב ציבורי, לא לחסום גישה
      if (currentRoute?.isPublic) {
        return false;
      }

      // בדיקה אם המשתמש יש לו הרשאות לנתיב הספציפי
      const hasRoutePermission = userPermission.hasPagePermission(selectedNav.link);
      const blocked = !hasRoutePermission;
      return blocked;
    };

    if (isBlocked()) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            p: 3
          }}
        >
          <Typography variant="h5" color="primary" component="h1" gutterBottom>
            אין הרשאות גישה
          </Typography>
          <Typography variant="body1" color="primary">
            אין לך הרשאות לצפות בדף זה. אנא פנה למנהל המערכת.
          </Typography>
        </Box>
      );
    }

    return props.children;
  };

  if (isSmallScreen) {
    return <MobileLayout children={handleInitialization()} />;
  }

  return (
    <Box sx={{ height: '100vh', backgroundColor: 'grey.A200', padding: 2, boxSizing: 'border-box' }}>
      {/* Hover menu - only show if there are multiple children */}
      {openMenuIndex !== null &&
        pagesList[openMenuIndex] &&
        pagesList[openMenuIndex]?.children &&
        pagesList[openMenuIndex]?.children?.length > 1 && (
          <HoverMenu
            openMenuIndex={openMenuIndex}
            menuChildren={
              pagesList[openMenuIndex]?.children?.filter(
                child => !child.link || userPermission?.hasPagePermission(child.link) || userPermission?.isAdmin
              ) || []
            }
            handleMouseLeave={handleMouseLeave}
            handleMenuClose={handleMenuClose}
            handleMouseEnterMenu={handleMouseEnterMenu}
            drawerWidth={drawerWidth}
          />
        )}
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          borderRadius: 6.1,
          backgroundColor: 'secondary.light'
        }}
      >
        <Box
          sx={{
            minWidth: drawerWidth,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 2,
            gap: 2,
            top: 0,
            height: '100%',
            boxSizing: 'border-box',
            overflow: 'auto'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              width: '100%',
              gap: 2,
              height: '60%'
            }}
          >
            <Box
              sx={{ width: 120, height: 85, mt: 3, alignContent: 'center', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <img src={reshet} alt="Logo" style={{ width: '100%', margin: 'auto' }} />
            </Box>

            <Divider sx={{ width: drawerWidth - 20 }} />

            <List
              sx={theme => ({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                height: '100%',
                py: 2,
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px'
                },
                '&::-webkit-scrollbar-track': {
                  background: theme.palette.secondary.main
                },
                '&::-webkit-scrollbar-thumb': {
                  background: theme.palette.primary.main,
                  borderRadius: '4px'
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: theme.palette.primary.main,
                  opacity: 0.8
                },
                scrollbarWidth: 'thin',
                scrollbarColor: `${theme.palette.primary.main} ${theme.palette.secondary.main}`,
                direction: 'rtl'
              })}
            >
              {pagesList.map((item, index) => {
                const hasChildren = item.children && item.children.length > 0;
                const hasSingleChild = hasChildren && item.children?.length === 1;
                const singleChildLink = hasSingleChild ? item.children?.[0]?.link : null;

                // בדיקת הרשאות - אם למשתמש אין הרשאה לעמוד, לא להציג אותו
                const hasPermission = hasChildren
                  ? item?.children?.some(child => !child.link || userPermission?.hasPagePermission(child?.link || ''))
                  : !item.link || userPermission?.hasPagePermission(item?.link || '');

                // אם למשתמש אין הרשאות כלל לפריט זה, לא להציג אותו
                if (!hasPermission && !userPermission?.isAdmin) {
                  return null;
                }

                return (
                  <Box
                    key={index}
                    sx={{ position: 'relative', width: '50%', display: 'flex', justifyContent: 'center' }}
                    onMouseEnter={event => {
                      // Only show hover menu if there are multiple children
                      if (hasChildren && !hasSingleChild) {
                        handleMenuOpen(event, index);
                      }
                    }}
                    onMouseLeave={() => {
                      // Only handle mouse leave if there are multiple children
                      if (hasChildren && !hasSingleChild) {
                        handleMouseLeave(index);
                      }
                    }}
                  >
                    <ListItem
                      onClick={() => {
                        if (hasSingleChild && singleChildLink) {
                          // If single child, navigate directly to child's link
                          navigate(singleChildLink);
                        } else if (!hasChildren && item.link) {
                          // If no children, navigate to item's own link
                          navigate(item.link);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 1,
                        cursor: 'pointer',
                        width: '50%'
                      }}
                    >
                      <ListItemIcon sx={{ display: 'flex', justifyContent: 'center', mb: 0.5, color: 'primary.main' }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.primary}
                        primaryTypographyProps={{
                          fontSize: 14,
                          lineHeight: '150%',
                          textAlign: 'center',
                          color: 'primary.main'
                        }}
                        sx={{ margin: 0 }}
                      />
                    </ListItem>
                  </Box>
                );
              })}
            </List>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1,
              width: '100%',
              mt: 1
            }}
          >
            <ProfileSection />

            <Divider sx={{ width: drawerWidth - 20 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: 9,
                  fontWeight: 300,
                  color: 'rgba(0, 0, 0, 0.6)',
                  display: 'block'
                }}
              >
                מרכז מידע
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: 9,
                  fontWeight: 300,
                  color: 'rgba(0, 0, 0, 0.6)',
                  display: 'block'
                }}
              >
                גרסה 1.0
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            maxHeight: '100%',
            backgroundColor: 'white',
            borderRadius: 6,
            boxShadow: '-27px 0px 17px -17px rgba(0, 0, 0, 0.25)',
            overflow: 'auto',
            minWidth: 0
          }}
        >
          <Box
            sx={{
              height: '100%',
              backgroundColor: props?.bgColor || 'white',
              borderRadius: 6,
              p: 1,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ flexShrink: 0, pb: 1 }}>
              <PageTitleHeader />
            </Box>
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>{handleInitialization()}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
